/**
 * We build lots of experiences with embedded iframes from the Cheetah agency.
 * These iframes post events to the parent window which we can listen for.
 * This allows us to do things such as resize the iframe when its content changes.
 */
import { useEffect } from "react";

export type CheetahEvent = Omit<MessageEvent, "data"> & { data: string };
export type CheetahEventHandler = (event: CheetahEventData) => void;

export type CheetahEventData =
  // Extend this as per your use-case (not an exhaustive list)
  | {
      action: "event";
      event?: "overlay:destroyed";
    }
  | {
      action: "enter--success";
    }
  | {
      action: "resize";
      event: "frame:resize";
      payload: {
        height: number;
      };
    };

const isCheetahEvent = (event: MessageEvent): event is CheetahEvent =>
  typeof event.data === "string" && event.data.startsWith("_NGX_");

const parseCheetahEventData = (event: CheetahEvent): CheetahEventData =>
  JSON.parse(event.data.replace("_NGX_", ""));

/**
 * Handles events fired by embedded Cheetah iframes.
 * @param handler The callback that gets executed for each event.
 * You should wrap this in `useCallback` to prevent unnecessary re-renders.
 */
export function useCheetahIFrameEvents(handler: CheetahEventHandler) {
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (isCheetahEvent(event)) {
        const data = parseCheetahEventData(event);
        handler(data);
      }
    };

    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [handler]);
}
